import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import {
  formatResultCount,
  ManagementFilterBar,
  ManagementPagination,
  ManagementSearchInput,
  ManagementSelectFilter,
} from 'components/ManagementFilters'
import { Snackbar } from 'components/Snackbar'
import { pageCardClasses } from 'components/AppLayout/styles'
import { MANAGEMENT_PAGE_SIZE } from 'constants/index'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { commonStrings, lineStrings } from 'constants/strings'
import {
  useBulkChannelOperations,
  useClientPagination,
  useLineChannelAccounts,
  useUnlinkLineChannel,
  useVerifyLineChannelWebhook,
} from 'hooks'
import { BulkFriendLimitModal } from 'pages/Line/BulkFriendLimitModal'
import { BulkStatusModal } from 'pages/Line/BulkStatusModal'
import { LineAccountsTable } from 'pages/Line/LineAccountsTable'
import { LineAccountsCardList } from 'pages/Line/LineAccountsCardList'
import { LineBulkActionBar } from 'pages/Line/LineBulkActionBar'
import {
  ELineChannelStatus,
  ELineWebhookStatus,
  type TBulkChannelOperationResponse,
  type TLineChannelAccount,
} from 'types'
import { matchesSearch } from 'utils'

const buildBulkResultMessage = (response: TBulkChannelOperationResponse) => {
  const strings = lineStrings.management.bulk.result
  if (response.failed === 0) {
    return strings.success.replace('{{count}}', String(response.successful))
  }
  return strings.partial
    .replace('{{successful}}', String(response.successful))
    .replace('{{failed}}', String(response.failed))
}

const buildBulkDetail = (
  response: TBulkChannelOperationResponse,
  accounts: TLineChannelAccount[],
) => {
  const strings = lineStrings.management.bulk.result
  const detailed = response.results.filter((result) => result.message)
  if (detailed.length === 0) return undefined
  const nameOf = (lineChannelId: number) => {
    const account = accounts.find((item) => item.id === lineChannelId)
    return account ? `@${account.basicId}` : `ID ${lineChannelId}`
  }
  return detailed
    .map((result) => {
      const prefix = strings.failureDetailPrefix.replace(
        '{{name}}',
        nameOf(result.lineChannelId),
      )
      return `${prefix}${result.message ?? ''}`.trim()
    })
    .join('\n')
}

type LineLocationState = {
  snackbarMessage?: string
  snackbarVariant?: 'success' | 'error'
}

type WebhookFilter = 'ENABLED' | 'DISABLED'

const lineStatusFilterOptions = [
  { value: ELineChannelStatus.ACTIVE, label: ELineChannelStatus.ACTIVE },
  { value: ELineChannelStatus.PAUSED, label: ELineChannelStatus.PAUSED },
  { value: ELineChannelStatus.FULL, label: ELineChannelStatus.FULL },
]

const webhookFilterOptions = [
  { value: 'ENABLED', label: lineStrings.management.filters.webhookEnabled },
  { value: 'DISABLED', label: lineStrings.management.filters.webhookDisabled },
] satisfies { value: WebhookFilter; label: string }[]

export default function LineManagement() {
  const location = useLocation()
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const [verifyTarget, setVerifyTarget] = useState<
    TLineChannelAccount | undefined
  >()
  const [isVerifySnackbarOpen, setIsVerifySnackbarOpen] = useState(false)
  const [verifySnackbarMessage, setVerifySnackbarMessage] = useState('')
  const [verifySnackbarVariant, setVerifySnackbarVariant] = useState<
    'success' | 'error'
  >('success')
  const [verifyActionMessage, setVerifyActionMessage] = useState<string | undefined>()
  const [unlinkTarget, setUnlinkTarget] = useState<
    TLineChannelAccount | undefined
  >()
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>()
  const [snackbarVariant, setSnackbarVariant] = useState<'success' | 'error'>('success')
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isBulkVerifyConfirmOpen, setIsBulkVerifyConfirmOpen] = useState(false)
  const [isBulkStatusModalOpen, setIsBulkStatusModalOpen] = useState(false)
  const [isBulkFriendLimitModalOpen, setIsBulkFriendLimitModalOpen] =
    useState(false)
  const [isBulkResultSnackbarOpen, setIsBulkResultSnackbarOpen] =
    useState(false)
  const [bulkResultMessage, setBulkResultMessage] = useState('')
  const [bulkResultVariant, setBulkResultVariant] = useState<
    'success' | 'warning' | 'error'
  >('success')
  const [bulkDetail, setBulkDetail] = useState<string | undefined>()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<ELineChannelStatus | ''>('')
  const [webhookFilter, setWebhookFilter] = useState<WebhookFilter | ''>('')
  const { accounts, loading, error } = useLineChannelAccounts()
  const {
    verify,
    isSubmitting: isVerifying,
    error: verifyError,
  } = useVerifyLineChannelWebhook({
    onSuccess: (response) => {
      const targetPrefix = verifyTarget ? `@${verifyTarget.basicId} ` : ''

      if (response.success) {
        setVerifySnackbarVariant('success')
        setVerifySnackbarMessage(
          `${targetPrefix}${lineStrings.management.messages.verifySuccess}`,
        )
        setVerifyActionMessage(undefined)
      } else {
        setVerifySnackbarVariant('error')
        setVerifySnackbarMessage(
          `${targetPrefix}${lineStrings.management.messages.verifyFailed}`,
        )
        setVerifyActionMessage(response.detail ?? undefined)
      }
      setVerifyTarget(undefined)
      setIsVerifySnackbarOpen(true)
    },
  })
  const {
    unlink,
    isSubmitting: isUnlinking,
    error: unlinkError,
  } = useUnlinkLineChannel({
    onSuccess: () => {
      setUnlinkTarget(undefined)
      setSnackbarMessage(lineStrings.management.messages.unlinkSuccess)
      setIsSnackbarOpen(true)
    },
  })
  const handleBulkResult = (response: TBulkChannelOperationResponse) => {
    const hasWarnings = response.results.some(
      (result) => result.success && result.message,
    )
    setBulkResultVariant(
      response.failed > 0 ? 'error' : hasWarnings ? 'warning' : 'success',
    )
    setBulkResultMessage(buildBulkResultMessage(response))
    setBulkDetail(buildBulkDetail(response, accounts))
    setIsBulkResultSnackbarOpen(true)
    setIsBulkVerifyConfirmOpen(false)
    setIsBulkStatusModalOpen(false)
    setIsBulkFriendLimitModalOpen(false)
    setSelectedIds(new Set())
  }
  const handleBulkError = () => {
    setIsBulkVerifyConfirmOpen(false)
    setIsBulkStatusModalOpen(false)
    setIsBulkFriendLimitModalOpen(false)
  }
  const {
    verify: bulkVerify,
    updateStatus: bulkUpdateStatus,
    updateFriendLimit: bulkUpdateFriendLimit,
    isProcessing: isBulkProcessing,
    error: bulkError,
  } = useBulkChannelOperations({
    onVerifySuccess: handleBulkResult,
    onUpdateStatusSuccess: handleBulkResult,
    onUpdateFriendLimitSuccess: handleBulkResult,
    onError: handleBulkError,
  })
  const alertMessage = useMemo(
    () => unlinkError ?? verifyError ?? bulkError ?? error,
    [unlinkError, verifyError, bulkError, error],
  )
  const selectedCount = selectedIds.size
  const filteredAccounts = useMemo(
    () =>
      accounts.filter((account) => {
        const basicId = account.basicId.replace(/^@/, '')

        if (!matchesSearch([basicId, `@${basicId}`], searchKeyword)) {
          return false
        }

        if (statusFilter && account.status !== statusFilter) {
          return false
        }

        if (
          webhookFilter === 'ENABLED' &&
          account.webhookStatus !== ELineWebhookStatus.VERIFIED
        ) {
          return false
        }

        if (
          webhookFilter === 'DISABLED' &&
          account.webhookStatus === ELineWebhookStatus.VERIFIED
        ) {
          return false
        }

        return true
      }),
    [accounts, searchKeyword, statusFilter, webhookFilter],
  )
  const isFiltering = Boolean(searchKeyword || statusFilter || webhookFilter)
  const countLabel = formatResultCount(filteredAccounts.length, accounts.length)
  const paginationResetKey = `${searchKeyword}\u0000${statusFilter}\u0000${webhookFilter}`
  const {
    currentPage,
    totalPage,
    pageItems: paginatedAccounts,
    goPrev,
    goNext,
  } = useClientPagination(
    filteredAccounts,
    MANAGEMENT_PAGE_SIZE,
    paginationResetKey,
  )
  const emptyMessage = isFiltering
    ? commonStrings.filteredEmpty
    : lineStrings.management.states.empty
  const verifyTargetName = verifyTarget ? `@${verifyTarget.basicId}` : ''
  const unlinkTargetName = unlinkTarget ? `@${unlinkTarget.basicId}` : ''

  const handleRequestVerify = (account: TLineChannelAccount) => {
    if (isVerifying) return
    setIsVerifySnackbarOpen(false)
    setVerifyActionMessage(undefined)
    setVerifyTarget(account)
  }

  const handleConfirmVerify = () => {
    if (!verifyTarget || isVerifying) return
    verify(verifyTarget.id)
  }

  const handleRequestUnlink = (account: TLineChannelAccount) => {
    setIsSnackbarOpen(false)
    setUnlinkTarget(account)
  }

  const handleConfirmUnlink = () => {
    if (!unlinkTarget || isUnlinking) return
    unlink(unlinkTarget.id)
  }

  const handleToggleRow = (lineChannelId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(lineChannelId)) {
        next.delete(lineChannelId)
      } else {
        next.add(lineChannelId)
      }
      return next
    })
  }

  const handleToggleAll = (checked: boolean) => {
    setSelectedIds(
      checked
        ? new Set(filteredAccounts.map((account) => account.id))
        : new Set(),
    )
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  const selectedAccounts = useMemo(
    () => accounts.filter((a) => selectedIds.has(a.id)),
    [accounts, selectedIds],
  )

  const selectedIdList = useMemo(
    () => selectedAccounts.map((a) => a.id),
    [selectedAccounts],
  )

  const handleConfirmBulkVerify = () => {
    if (isBulkProcessing || selectedIdList.length === 0) return
    setIsBulkResultSnackbarOpen(false)
    bulkVerify(selectedIdList)
  }

  const handleSubmitBulkStatus = (status: ELineChannelStatus) => {
    if (isBulkProcessing || selectedIdList.length === 0) return
    setIsBulkResultSnackbarOpen(false)
    bulkUpdateStatus(selectedIdList, status)
  }

  const handleSubmitBulkFriendLimit = (friendLimit: number) => {
    if (isBulkProcessing || selectedIdList.length === 0) return
    setIsBulkResultSnackbarOpen(false)
    bulkUpdateFriendLimit(selectedIdList, friendLimit)
  }

  const handleOpenBulkVerify = () => {
    setIsBulkResultSnackbarOpen(false)
    setIsBulkVerifyConfirmOpen(true)
  }

  const handleOpenBulkStatus = () => {
    setIsBulkResultSnackbarOpen(false)
    setIsBulkStatusModalOpen(true)
  }

  const handleOpenBulkFriendLimit = () => {
    setIsBulkResultSnackbarOpen(false)
    setIsBulkFriendLimitModalOpen(true)
  }

  useEffect(() => {
    if (!verifyError) return
    setVerifyTarget(undefined)
  }, [verifyError])

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(accounts.map((account) => account.id))
      let changed = false
      const next = new Set<number>()
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id)
        } else {
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [accounts])

  useEffect(() => {
    const state = location.state as LineLocationState | null
    const message = state?.snackbarMessage
    if (!message) return

    setSnackbarMessage(message)
    setSnackbarVariant(state?.snackbarVariant ?? 'success')
    setIsSnackbarOpen(true)
    navigate(orgPathFor(routes.LINE), { replace: true, state: null })
  }, [location.state, navigate, orgPathFor])

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {lineStrings.management.title}
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
            <ManagementPagination
              currentPage={currentPage}
              totalPage={totalPage}
              loading={loading}
              onPrevious={goPrev}
              onNext={goNext}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => navigate(orgPathFor(routes.LINE_CONNECT))}
            >
              {lineStrings.management.connectButton}
            </Button>
          </div>
        </header>

        {alertMessage && (
          <div className="mt-6">
            <Alert>{alertMessage}</Alert>
          </div>
        )}

        {verifyActionMessage && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <p className="text-sm text-amber-800">{verifyActionMessage}</p>
          </div>
        )}

        <ManagementFilterBar>
          <ManagementSearchInput
            label={commonStrings.filters.searchLabel}
            placeholder={lineStrings.management.filters.searchPlaceholder}
            value={searchKeyword}
            onChange={setSearchKeyword}
            onClear={() => setSearchKeyword('')}
          />
          <ManagementSelectFilter
            label={lineStrings.management.filters.status}
            value={statusFilter}
            options={lineStatusFilterOptions}
            onChange={setStatusFilter}
            onClear={() => setStatusFilter('')}
          />
          <ManagementSelectFilter
            label={lineStrings.management.filters.webhookStatus}
            value={webhookFilter}
            options={webhookFilterOptions}
            onChange={setWebhookFilter}
            onClear={() => setWebhookFilter('')}
          />
        </ManagementFilterBar>

        {selectedCount > 0 && (
          <div className="mt-6 hidden lg:block">
            <LineBulkActionBar
              selectedCount={selectedCount}
              isProcessing={isBulkProcessing}
              onVerify={handleOpenBulkVerify}
              onChangeStatus={handleOpenBulkStatus}
              onChangeFriendLimit={handleOpenBulkFriendLimit}
              onClearSelection={handleClearSelection}
            />
          </div>
        )}

        <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
          <LineAccountsTable
            accounts={paginatedAccounts}
            loading={loading}
            emptyMessage={emptyMessage}
            onRequestVerify={handleRequestVerify}
            onRequestUnlink={handleRequestUnlink}
            isVerifying={isVerifying}
            unlinkingLineChannelId={isUnlinking ? unlinkTarget?.id : undefined}
            selectedIds={selectedIds}
            onToggleRow={handleToggleRow}
            onToggleAll={handleToggleAll}
            isBulkProcessing={isBulkProcessing}
          />
        </div>
        <div className="mt-6 lg:hidden">
          <LineAccountsCardList
            accounts={paginatedAccounts}
            loading={loading}
            emptyMessage={emptyMessage}
            onRequestVerify={handleRequestVerify}
            onRequestUnlink={handleRequestUnlink}
            isVerifying={isVerifying}
            unlinkingLineChannelId={isUnlinking ? unlinkTarget?.id : undefined}
            className="md:grid-cols-2"
          />
        </div>
      </section>
      <ConfirmModal
        open={Boolean(verifyTarget)}
        textTitle={lineStrings.management.confirm.verifyTitle}
        textMessage={`${verifyTargetName}\n${lineStrings.management.confirm.verifyDescription}`}
        textCancel={commonStrings.cancel}
        textConfirm={lineStrings.management.confirm.verifyConfirm}
        onClose={() => {
          if (isVerifying) return
          setVerifyTarget(undefined)
        }}
        onConfirm={handleConfirmVerify}
        isLoading={isVerifying}
      />
      <ConfirmModal
        open={Boolean(unlinkTarget)}
        textTitle={lineStrings.management.confirm.unlinkTitle}
        textMessage={`${unlinkTargetName}\n${lineStrings.management.confirm.unlinkDescription}`}
        textCancel={commonStrings.cancel}
        textConfirm={lineStrings.management.confirm.unlinkConfirm}
        confirmButtonVariant="outline"
        confirmButtonClassName="border-[#F35D5D] bg-[#F35D5D] text-white shadow-[0_16px_30px_rgba(243,93,93,0.32)] hover:border-[#E34E4E] hover:bg-[#E34E4E] hover:text-white hover:shadow-[0_20px_40px_rgba(243,93,93,0.38)] disabled:shadow-none"
        onClose={() => {
          if (isUnlinking) return
          setUnlinkTarget(undefined)
        }}
        onConfirm={handleConfirmUnlink}
        isLoading={isUnlinking}
      />
      <ConfirmModal
        open={isBulkVerifyConfirmOpen}
        textTitle={lineStrings.management.bulk.verifyConfirm.title}
        textMessage={lineStrings.management.bulk.verifyConfirm.description.replace(
          '{{count}}',
          String(selectedCount),
        )}
        textCancel={commonStrings.cancel}
        textConfirm={lineStrings.management.bulk.verifyConfirm.confirm}
        onClose={() => {
          if (isBulkProcessing) return
          setIsBulkVerifyConfirmOpen(false)
        }}
        onConfirm={handleConfirmBulkVerify}
        isLoading={isBulkProcessing}
      />
      <BulkStatusModal
        open={isBulkStatusModalOpen}
        selectedCount={selectedCount}
        isSubmitting={isBulkProcessing}
        onClose={() => setIsBulkStatusModalOpen(false)}
        onSubmit={handleSubmitBulkStatus}
      />
      <BulkFriendLimitModal
        open={isBulkFriendLimitModalOpen}
        selectedCount={selectedCount}
        isSubmitting={isBulkProcessing}
        onClose={() => setIsBulkFriendLimitModalOpen(false)}
        onSubmit={handleSubmitBulkFriendLimit}
      />
      <Snackbar
        open={isBulkResultSnackbarOpen}
        message={
          bulkDetail ? (
            <div className="grid gap-1">
              <span className="font-semibold">{bulkResultMessage}</span>
              <span className="whitespace-pre-wrap text-xs">{bulkDetail}</span>
            </div>
          ) : (
            bulkResultMessage
          )
        }
        variant={bulkResultVariant}
        position="top-center"
        autoHideDuration={
          bulkResultVariant === 'success' && !bulkDetail ? 3000 : 6000
        }
        onClose={() => setIsBulkResultSnackbarOpen(false)}
      />
      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        variant={snackbarVariant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
      />
      <Snackbar
        open={isVerifySnackbarOpen}
        message={verifySnackbarMessage}
        variant={verifySnackbarVariant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsVerifySnackbarOpen(false)}
      />
    </>
  )
}
